import { useEffect, useState } from 'react';
import {
  DataGrid,
  GridActionsCellItem,
} from '@mui/x-data-grid';
import {
  Box,
  CircularProgress,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';

import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';

import { authFetch } from './utils/api';
import { authFetchRequest } from './utils/api';
import { getCookie } from './utils/cookies';
import { alertC, confirmC } from './components/dialogService';
import { useCrmSettings } from './CRM/SimpleCRM/CrmSettingsContext';
import HistoryIcon from '@mui/icons-material/History';
import { get_crm_user_id, getPermissions } from './CRM/SimpleCRM/crmUtils';
import EditCustomerDialog from './EditCustomerDialog';
import { useNavigate } from 'react-router-dom';
import './customers_base.css';




const Customers = () => {
  const navigate = useNavigate();
  const { settings: crmSettings } = useCrmSettings();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '', email: '', phone: '', discount: '0', products: '',
    netTerms: '', fullName: '', mobilePhone: '', address1: '',
    address2: '', city: '', province: '', country: '', postalCode: ''
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [perms, setPerms] = useState(null);
  const [searchText, setSearchText] = useState('');

  // Single init: resolve crmUserId → compute permissions → fetch customers
  useEffect(() => {
    const init = async () => {
      const crmUserId = await get_crm_user_id();
      const p = getPermissions({
        crmUserId,
        crmRole:          getCookie('crm_role') || '',
        userRole:         getCookie('user_role') || '',
        userId:           getCookie('user_id'),
        quoteManagerOnly: crmSettings.quote_manager_only,
        commonCustomers:  crmSettings.common_customers,
        crmDefined:       crmSettings.external_crm !== 'none',
        simpleCrmEnabled: crmSettings.simple_crm_enabled,
      });
      setPerms(p);

      setLoading(true);
      try {
        let data;
        if (p.isCrmSales && crmSettings.common_customers) {
          data = await authFetch(`/customers/?fromQuotes=true&fkUserId=${getCookie('user_id')}`);
        } else if (p.seeAllCustomers) {
          data = await authFetch('/customers/');
        } else {
          data = await authFetch(`/customers/?fkUserId=${getCookie('user_id')}`);
        }
        if (!Array.isArray(data)) throw new Error('Invalid response format');
        setRows(data);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
        if (err.message.includes('401')) {
          alert('Session expired. Please login again.');
          navigate('/login');
        } else {
          alert('Failed to load customers. Please try again later.');
        }
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [crmSettings.quote_manager_only, crmSettings.common_customers, crmSettings.external_crm, crmSettings.simple_crm_enabled]); // re-run when settings load from DB

const filteredCustomers = rows.filter((customer) => {
  if (!searchText) return true;
  const term = searchText.toLowerCase().trim();

  const safeLower = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase();
  };

  return (
    safeLower(customer.id).includes(term) ||
    safeLower(customer.name).includes(term) ||
    safeLower(customer.email).includes(term) ||
    safeLower(customer.phone).includes(term) ||
    safeLower(customer.discount).includes(term) ||
    safeLower(customer.products).includes(term) ||
    safeLower(customer.netTerms).includes(term) ||
    safeLower(customer.fullName).includes(term) ||
    safeLower(customer.mobilePhone).includes(term) ||
    safeLower(customer.address1).includes(term) ||
    safeLower(customer.address2).includes(term) ||
    safeLower(customer.city).includes(term) ||
    safeLower(customer.province).includes(term) ||
    safeLower(customer.country).includes(term) ||
    safeLower(customer.postalCode).includes(term) ||
    safeLower(customer.fkUserId).includes(term)
  );
});

  // Delete row
  const handleDeleteClick = (id) => async () => {
    try {
      const row = rows.find((r) => r.id === id);
      if (!row) {
        await alertC("Row not found for ID:", id);
        return;
      }
      if (parseInt(getCookie('user_id')) !== parseInt(row.fkUserId)) {
        await alertC("You don't have permission to delete this item.");
        return;
      } 
      const confirmed = await confirmC("Are you sure you want to delete this customer?");
      if (!confirmed) return;
      await authFetchRequest(`/customers/${id}`, null, "DELETE");
      setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      console.log(`Customer ${id} deleted`);
    } catch (err) {
      console.error("Delete failed:", err);
      await alertC("Failed to delete customer: " + err.message);
    }
  };
   

  const handleAddCustomer = async () => {
    try {
      // Retrieve the user ID from cookies.
      const userIdCookie = getCookie("user_id");
      if (!userIdCookie) {
        await alertC("User ID not found in cookies!");
        return;
      }
      const fkUserId = parseInt(userIdCookie, 10);
      if (isNaN(fkUserId)) {
        await alertC("User ID is invalid!");
        return;
      }
      console.log("User ID:", fkUserId);
  
      // Prepare the payload for the new customer.
      // newCustomer should be your local state holding the customer form data.
      const payload = { ...newCustomer, fkUserId };
      console.log("Payload to send:", payload);
      const added =await authFetchRequest(`/customers/`, payload);
      console.log("Customer successfully added:", added);
  
      // Update your local state with the new customer.
      setRows((prev) => [...prev, added]);
      setNewDialogOpen(false);
      setNewCustomer({
        name: "",
        email: "",
        phone: "",
        discount: "0",
        products: "",
        netTerms: "",
        fullName: "",
        mobilePhone: "",
        address1: "",
        address2: "",
        city: "",
        province: "",
        country: "",
        postalCode: "",
      });
    } catch (err) {
      console.error("Failed to add customer:", err);
      await alertC("Failed to add customer: " + err.message);
    }
  };

  
  const columns = [
    { field: 'id', headerName: 'ID', width: 40 },
    { field: 'name', headerName: 'Name', width: 100 },
    { field: 'email', headerName: 'Email', width: 170 },
    { field: 'phone', headerName: 'Phone', width: 70 },
    { field: 'discount', headerName: 'Discount', width: 80 },
    { field: 'products', headerName: 'Products', width: 80 },
    { field: 'netTerms', headerName: 'Net Terms', width: 80 },
    { field: 'fullName', headerName: 'Full Name', width: 120 },
    { field: 'mobilePhone', headerName: 'Mobile', width: 90 },
    { field: 'address1', headerName: 'Address1', width: 120 },
    { field: 'address2', headerName: 'Address2', width: 20 },
    { field: 'city', headerName: 'City', width: 100 },
    { field: 'province', headerName: 'Province', width: 70 },
    { field: 'country', headerName: 'Country', width: 70 },
    { field: 'postalCode', headerName: 'Zip', width: 80 },
    { field: 'salesman_name', headerName: 'Salesman', width: 120,
      valueGetter: (value, row) => value || row.user_name || row.fkUserId },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Del',
      width: 100,
      getActions: ({ id }) => [
        ...(crmSettings.simple_crm_enabled ? [
          <GridActionsCellItem
            icon={<HistoryIcon />}
            label="History"
            onClick={() => navigate(`/customer-history/${id}`)}
          />,
        ] : []),
        ...(perms?.canDeleteCustomer ? [
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={handleDeleteClick(id)}
          />,
        ] : []),
      ],
    },
  ];

  return (
    <Box sx={{ width: '100%' }} className="content-container">
      {/*<Typography variant="h5" gutterBottom>Customers</Typography>*/}
      <h3>Customers</h3>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        {/* Add Customer button */}
        {perms?.canAddCustomer && (
          <Button
            variant="contained"
            color="success"
            startIcon={<Add />}
            onClick={() => setNewDialogOpen(true)}
          >
            Add Customer
          </Button>
        )}

        {/* Edit Customer button — appears only when a row is selected */}
        {customerToEdit && perms?.canEditCustomer && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Edit />}
            onClick={() => setShowEditDialog(true)}
          >
            Edit Customer
          </Button>
        )}
      </Stack>

      <Paper
        elevation={1}
        sx={{
          p: '2px 4px',
          display: 'flex',
          alignItems: 'center',
          width: 400,
          mb: 2,
          borderRadius: 3,
        }}
      >
        <SearchIcon sx={{ color: 'action.active', mx: 1 }} />
        <InputBase
          placeholder="Search customers..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ ml: 1, flex: 1 }}
        />
      </Paper>  
      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={filteredCustomers}   /* rows={rows} ← change this line */
          columns={columns}
          columnVisibilityModel={{
            discount: false,
            products: false,
            netTerms: false,
            address2: false,
          }}
          pageSize={10}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => setPaginationModel(model)}
          rowsPerPageOptions={[10, 20, 50]} // 👈  dropdown options
          pageSizeOptions={[10, 20, 50]}    // 👈 fallback for older versions

          onRowClick={(params) => setCustomerToEdit(params.row)}
          rowSelectionModel={customerToEdit ? [customerToEdit.id] : []}
          checkboxSelection={false}
          sx={{
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: '#e3f2fd',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: '#bbdefb',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
            },
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'id', sort: 'desc' }],
            },
          }}
        />
      )}

      {/* Add Customer Dialog */}
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          {Object.keys(newCustomer).map((key) => (
            <TextField
              key={key}
              label={key}
              fullWidth
              margin="dense"
              value={newCustomer[key]}
              onChange={(e) => setNewCustomer({ ...newCustomer, [key]: e.target.value })}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddCustomer}>Add</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Customer Dialog */}
      <EditCustomerDialog
        open={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setCustomerToEdit(null);
        }}
        customer={customerToEdit}
        onCustomerUpdated={(updatedCustomer) => {
          setRows(prev => prev.map(r => r.id === updatedCustomer.id ? updatedCustomer : r));
          setCustomerToEdit(updatedCustomer); // keep selection
        }}
      />
    </Box>
  );
};

export default Customers;
